import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ModuleStatus, ModuleKey } from '../constants/module-status.enum';
import { AppConstants } from '../constants/constants';

/**
 * Centralized Permission Service
 * Manages module access statuses and permissions across the application
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  // BehaviorSubject for reactive module status updates
  private readonly moduleStatuses = new BehaviorSubject<Record<string, ModuleStatus>>({});

  // Public observable for components to subscribe
  public moduleStatuses$: Observable<Record<string, ModuleStatus>> = this.moduleStatuses.asObservable();

  constructor() {
    // Load cached statuses from localStorage on service initialization
    this.loadFromStorage();
  }

  /**
   * Sets module statuses and persists to localStorage
   * @param statuses Record of module keys and their status values
   */
  setModuleStatuses(statuses: Record<string, ModuleStatus>): void {
    this.moduleStatuses.next(statuses);
    this.persistToStorage(statuses);
  }

  /**
   * Gets the current status for a specific module
   * @param moduleKey The module key to check
   * @returns ModuleStatus or null if not found
   */
  getModuleStatus(moduleKey: ModuleKey): ModuleStatus | null {
    const currentStatuses = this.moduleStatuses.value;
    return currentStatuses[moduleKey] || null;
  }

  /**
   * Checks if a module has active access
   * @param moduleKey The module key to check
   * @returns true if module status is ACTIVE
   */
  hasModuleAccess(moduleKey: ModuleKey): boolean {
    return this.getModuleStatus(moduleKey) === ModuleStatus.ACTIVE;
  }

  /**
   * Checks if a module is hidden
   * @param moduleKey The module key to check
   * @returns true if module status is HIDDEN
   */
  isModuleHidden(moduleKey: ModuleKey): boolean {
    return this.getModuleStatus(moduleKey) === ModuleStatus.HIDDEN;
  }

  /**
   * Checks if a module is coming soon
   * @param moduleKey The module key to check
   * @returns true if module status is COMING_SOON
   */
  isModuleComingSoon(moduleKey: ModuleKey): boolean {
    return this.getModuleStatus(moduleKey) === ModuleStatus.COMING_SOON;
  }

  /**
   * Checks if a module requires upgrade
   * @param moduleKey The module key to check
   * @returns true if module status is UPGRADE_REQUIRED
   */
  isModuleUpgradeRequired(moduleKey: ModuleKey): boolean {
    return this.getModuleStatus(moduleKey) === ModuleStatus.UPGRADE_REQUIRED;
  }

  /**
   * Gets all current module statuses
   * @returns Record of all module statuses
   */
  getAllModuleStatuses(): Record<string, ModuleStatus> {
    return { ...this.moduleStatuses.value };
  }

  /**
   * Clears all module statuses from memory and storage
   */
  clearModuleStatuses(): void {
    this.moduleStatuses.next({});
    localStorage.removeItem(AppConstants.PRISISTED_KEYS.MODULE_STATUSES);
  }

  /**
   * Loads module statuses from localStorage
   * Called on service initialization
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(AppConstants.PRISISTED_KEYS.MODULE_STATUSES);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, ModuleStatus>;
        this.moduleStatuses.next(parsed);
      }
    } catch (error) {
      console.error('Failed to load module statuses from localStorage:', error);
      this.moduleStatuses.next({});
    }
  }

  /**
   * Persists module statuses to localStorage
   * Stores in new format using ModuleKey enums
   */
  private persistToStorage(statuses: Record<string, ModuleStatus>): void {
    try {
      // Store in new format: ModuleKey -> ModuleStatus
      localStorage.setItem(AppConstants.PRISISTED_KEYS.MODULE_STATUSES, JSON.stringify(statuses));
    } catch (error) {
      console.error('Failed to persist module statuses to localStorage:', error);
    }
  }


  /**
   * Checks if user has permission based on permission string array
   * @param permissions Array of permission strings
   * @returns true if user has any of the permissions
   */
  hasPermission(permissions: string[]): boolean {
    try {
      const userPermissions = localStorage.getItem(AppConstants.PRISISTED_KEYS.USER_PERMISSIONS);
      if (!userPermissions) return false;

      const parsedPermissions: string[] = JSON.parse(userPermissions);
      return permissions.some(permission => parsedPermissions.includes(permission));
    } catch (error) {
      console.error('Failed to check permissions:', error);
      return false;
    }
  }
}
